import axios from "axios";
import Vue from "vue";
import Utils from "./utils";
import VueApp from "./main";
import BASIC_ATTRIBUTES from "./enums/basic_attributes";
import TRANSLATIONS from "./enums/translations";
import conceptsStore from "./concepts-store";

class StoreConfiguration {

    constructor() {
        this.state = {
            current: {},
            isCurrentLoaded: false,
            default: {},
            concept: {},
            rootConfiguration: undefined,
            configCache: {},
        };
    }

    /**
     * Get default configuration.
     */
    async getDefaultConfiguration() {
        let params = { "terminologyName": "default" };
        this.state.default = await this.loadConfiguration(params);
    }

    /**
     * Get concept configuration.
     */
    async getConceptConfiguration(terminoName) {
        let params;
        if (terminoName) {
            params = { "terminologyName": terminoName };
        } else {
            params = { "terminologyName": conceptsStore.state.terminologyId };
        }

        this.state.concept = await this.loadConfiguration(params);
    }

    /**
     * Get concept configuration.
     */
    async getConceptConfigurationById(terminologyId) {
        let params = { "terminologyName": terminologyId };
        this.state.concept = await this.loadConfiguration(params);
    }

    /**
     * Load configuration on server.
     */
    async loadConfiguration(params) {
        if (params['terminologyName'] && this.state.configCache[params['terminologyName']]) {
            return this.state.configCache[params['terminologyName']];
        }

        return await axios.get(
            Vue.prototype.$searchResultManager.wordPressBaseUrl + '/wp-json/ans/concepts/configuration/get',
            { params: params }
        ).then((response) => {
            this.state.configCache[params['terminologyName']] = response.data;
            return response.data;
        }).catch(() => {
            this.state.configCache[params['terminologyName']] = {};
            return {};
        });
    }

    /**
     * Init the configuration of each concepts tree nodes.
     *
     * @param conceptsTree
     * @returns {*}
     */
    initConceptsTreeConfiguration(conceptsTree) {

        let configuration = this.overrideConfiguration(this.state.default.default_termino, this.state.concept.default_termino);
        this.state.rootConfiguration = configuration;

        return conceptsTree.map((concept) => {
            return this.browseRecursivelyNestedConceptsAndAddConfiguration(concept, []);
        });
    }

    /**
     * Return the max number of children to show in each conceptTree branches.
     *
     * @returns integer
     */
    getChildrenLimit() {
        let configuration = this.overrideConfiguration(this.state.default.default_termino, this.state.concept.default_termino);

        if (configuration.concepts_arborescence?.children_limit) {
            return configuration.concepts_arborescence.children_limit;
        }

        return 9999;
    }

    /**
     * Return the current concept configuration.
     *
     * @returns configuration
     */
    getCurrentConceptConfiguration(treeClass, conceptUri) {
        let config = this.overrideConfiguration(this.state.default.default_termino, this.state.concept.default_termino);
        if (treeClass) {
            const pathUris = this.pathFromTreeClass(treeClass, conceptUri);
            pathUris.forEach((pathUri, index) => {
                if (this.state.concept[pathUri]) {
                    const isSelf = index === pathUris.length - 1;
                    const isDirectChildren = index === pathUris.length - 2;
                    config = this.overrideConfiguration(config, this.state.concept[pathUri], isSelf, isDirectChildren);
                }
            });
        }
        return config;
    }

    pathFromTreeClass(treeClass, conceptUri) {
        let pathUris = [];

        let uri = conceptUri;
        do {
            let treeClassItem = treeClass.find(e => e.id === uri);
            uri = undefined;
            if (treeClassItem) {
                pathUris.splice(0, 0, treeClassItem.id);
                if (treeClassItem.parents && treeClassItem.parents.length > 0) {
                    uri = treeClassItem.parents[0];
                }
            }
        } while (uri);

        return pathUris;
    }

    /**
     * Return the concept confirguration for tree root.
     */
    getConceptTreeRootConfiguration() {
        return this.overrideConfiguration(this.state.default.default_termino, this.state.concept.default_termino);
    }

    /**
     * Browse nested concepts then retrieve and add configuration.
     * If the concept does not have a specific configuration, then get the parent configuration and so on.
     *
     * @param parentConcept
     * @returns {*}
     * @see addConfigurationToEachNodes()
     */
    browseRecursivelyNestedConceptsAndAddConfiguration(concept, parentUris) {

        let pathUris = JSON.parse(JSON.stringify(parentUris));
        concept.pathUris = pathUris;
        pathUris.push(concept.id);

        if (!concept.configuration) {
            concept.configuration = JSON.parse(JSON.stringify(this.state.rootConfiguration)); // Deep copy

            pathUris.forEach((pathUri, index) => {
                if (this.state.concept[pathUri]) {
                    const isSelf = index === pathUris.length - 1;
                    const isDirectChildren = index === pathUris.length - 2;
                    concept.configuration = this.overrideConfiguration(concept.configuration, this.state.concept[pathUri], isSelf, isDirectChildren);
                }
            });
        }

        if (concept.children && concept.children.length > 0) {
            concept.children.map((childConcept) => {
                this.browseRecursivelyNestedConceptsAndAddConfiguration(childConcept, pathUris);
            });
        }

        return concept;
    }

    /**
     * Override the parent configuration by adding the specific concept configuration.
     * To be taken into account, the property need to be defined and have parameter(s).
     * Otherwise, the parent configuration is kept.
     *
     * @param parentConfiguration
     * @param conceptConfiguration
     * @returns {any}
     */
    overrideConfiguration(parentConfiguration, conceptConfiguration, isSelf, isDirectChildren) {

        if (!parentConfiguration && !conceptConfiguration) { return {}; }

        if (!parentConfiguration) { return conceptConfiguration; }

        const configuration = JSON.parse(JSON.stringify(parentConfiguration)); // Copy object without reference.

        this.overrideConfigurationParamWithAppyTo(conceptConfiguration, configuration, 'page_title', isSelf, isDirectChildren);

        this.overrideConfigurationParamWithAppyTo(conceptConfiguration && conceptConfiguration.concepts_arborescence ? conceptConfiguration.concepts_arborescence : null, configuration.concepts_arborescence, 'ordre', false, isSelf);
        this.overrideConfigurationParamWithAppyTo(conceptConfiguration && conceptConfiguration.concepts_arborescence ? conceptConfiguration.concepts_arborescence : null, configuration.concepts_arborescence, 'label_affichage', isSelf, isDirectChildren);
        this.overrideConfigurationParam(conceptConfiguration && conceptConfiguration.concepts_arborescence ? conceptConfiguration.concepts_arborescence : null, configuration.concepts_arborescence, 'children_limit');

        this.overrideConfigurationParamWithAppyTo(conceptConfiguration && conceptConfiguration.concepts_details ? conceptConfiguration.concepts_details : null, configuration.concepts_details, 'sections', isSelf, isDirectChildren);
        this.overrideConfigurationParamWithAppyTo(conceptConfiguration && conceptConfiguration.concepts_details && conceptConfiguration.concepts_details.attributs ? conceptConfiguration.concepts_details.attributs : null, configuration.concepts_details && configuration.concepts_details.attributs ? configuration.concepts_details.attributs : null, 'ordre', isSelf, isDirectChildren);
        this.overrideConfigurationParamWithAppyTo(conceptConfiguration && conceptConfiguration.concepts_details && conceptConfiguration.concepts_details.attributs ? conceptConfiguration.concepts_details.attributs : null, configuration.concepts_details && configuration.concepts_details.attributs ? configuration.concepts_details.attributs : null, 'masques', isSelf, isDirectChildren);
        this.overrideConfigurationParamWithAppyTo(conceptConfiguration && conceptConfiguration.concepts_details && conceptConfiguration.concepts_details.attributs ? conceptConfiguration.concepts_details.attributs : null, configuration.concepts_details && configuration.concepts_details.attributs ? configuration.concepts_details.attributs : null, 'label_affichage', isSelf, isDirectChildren);

        this.overrideConfigurationParamWithAppyTo(conceptConfiguration && conceptConfiguration.concepts_details && conceptConfiguration.concepts_details.affichage ? conceptConfiguration.concepts_details.affichage : null, configuration.concepts_details && configuration.concepts_details.affichage ? configuration.concepts_details.affichage : null, 'ordre_langues', isSelf, isDirectChildren);
        this.overrideConfigurationParamWithAppyTo(conceptConfiguration && conceptConfiguration.concepts_details && conceptConfiguration.concepts_details.affichage ? conceptConfiguration.concepts_details.affichage : null, configuration.concepts_details && configuration.concepts_details.affichage ? configuration.concepts_details.affichage : null, 'avec_accordeon', isSelf, isDirectChildren);
        this.overrideConfigurationParamWithAppyTo(conceptConfiguration && conceptConfiguration.concepts_details && conceptConfiguration.concepts_details.affichage ? conceptConfiguration.concepts_details.affichage : null, configuration.concepts_details && configuration.concepts_details.affichage ? configuration.concepts_details.affichage : null, 'ordre_valeurs', isSelf, isDirectChildren);
        this.overrideConfigurationParamWithAppyTo(conceptConfiguration && conceptConfiguration.concepts_details && conceptConfiguration.concepts_details.affichage ? conceptConfiguration.concepts_details.affichage : null, configuration.concepts_details && configuration.concepts_details.affichage ? configuration.concepts_details.affichage : null, 'structure_libs', isSelf, isDirectChildren);
        this.overrideConfigurationParamWithAppyTo(conceptConfiguration && conceptConfiguration.concepts_details && conceptConfiguration.concepts_details.affichage ? conceptConfiguration.concepts_details.affichage : null, configuration.concepts_details && configuration.concepts_details.affichage ? configuration.concepts_details.affichage : null, 'valeurs_masquees', isSelf, isDirectChildren);

        return configuration;
    }

    overrideConfigurationParamWithAppyTo(sourceConfigObject, targetConfigObject, propertyName, isSelf, isDirectChildren) {
        if (sourceConfigObject) {
            const applyTo = sourceConfigObject[propertyName + '_apply_to'] || ['uri', 'children_all'];
            if ((applyTo.indexOf('self') >= 0 && isSelf) || (applyTo.indexOf('children_self') >= 0 && isDirectChildren) || (applyTo.indexOf('children_all') >= 0 && !isSelf)) {
                this.overrideConfigurationParam(sourceConfigObject, targetConfigObject, propertyName)
            }
        }
    }

    overrideConfigurationParam(sourceConfigObject, targetConfigObject, propertyName) {
        if (sourceConfigObject && sourceConfigObject[propertyName]) {
            let value = JSON.parse(JSON.stringify(sourceConfigObject[propertyName])); // Deep copy
            targetConfigObject[propertyName] = (value &&
                (typeof value === 'number'
                    || (typeof value === 'string' && value.length > 0)
                    || (Object.keys(value).length > 0 && ((value.pattern && value.pattern.length > 0) || !value.variables || Object.keys(value.variables).length > 0))))
                ? value
                : targetConfigObject[propertyName];
        }
    }

    getConfTreeNodeLabel(conceptId, configuration, props) {
        return this.getConceptLabelByConfig(conceptId, configuration && configuration.concepts_arborescence ? configuration.concepts_arborescence.label_affichage : null, props);
    }

    getConceptLabelByConfig(conceptId, labelConfig, props) {

        if (!(labelConfig && labelConfig.variables && Object.keys(labelConfig.variables).length > 0)) {
            return this.getPropertyValueFromConceptsProps(conceptId, BASIC_ATTRIBUTES.label, VueApp.$route.query.lang, props);
        }

        const REGEX_URL = new RegExp('^(https?:\\/\\/)', 'i');
        let label = labelConfig.pattern;

        Object.entries(labelConfig.variables).forEach(([key, param]) => {

            // Get the URI correspondence
            if (!REGEX_URL.test(param.value) && BASIC_ATTRIBUTES[param.value]) param.value = BASIC_ATTRIBUTES[param.value];

            // Get the text value.
            let propertyText = this.getPropertyValueFromConceptsProps(conceptId, param.value, VueApp.$route.query.lang, props, false);

            // Change the case of the text.
            if (propertyText !== "") {
                switch (param.case) {
                    case "lower":
                        propertyText = propertyText.toLowerCase();
                        break;
                    case "upper":
                        propertyText = propertyText.toUpperCase();
                        break;
                    case "camel":
                        propertyText = Utils.camelize(propertyText);
                        break;
                    default:
                }
            }

            label = label.replace(key, propertyText);
        });

        return label;
    }

    /**
     * Compare concept tree node(Object) by parameters defined in the concept's configuration.
     * @param {*} a Object a
     * @param {*} b Object b
     * @param {*} configuration An object which contains incremental numbered key and their values.
     * @param {*} lang Language code (2 letters)
     * @param {*} key key in configuration, it's an integer
     * @returns
     */
    compareByConfig(a, b, configuration, lang, key) {
        if (key in configuration) {
            const REGEX_URL = new RegExp('^(https?:\\/\\/)', 'i');
            // Get the parameters
            const param = configuration[key];

            // Get the URI correspondence
            if (!REGEX_URL.test(param.attribut)) param.attribut = BASIC_ATTRIBUTES[param.attribut];

            const isNumeric = param.type === 'alphanum' || param.type === 'num';

            if (param.ordre === 'asc') {
                const resultAsc = Utils.compareString(
                    this.getPropertyValueFromConceptsProps(a.id, param.attribut, lang, a.props, false),
                    this.getPropertyValueFromConceptsProps(b.id, param.attribut, lang, b.props, false),
                    lang ? lang : 'en',
                    isNumeric);
                return resultAsc === 0 ? this.compareByConfig(a, b, configuration, lang, key + 1) : resultAsc;
            }
            if (param.ordre === 'desc') {
                const resultDesc = Utils.compareString(
                    this.getPropertyValueFromConceptsProps(b.id, param.attribut, lang, b.props, false),
                    this.getPropertyValueFromConceptsProps(a.id, param.attribut, lang, a.props, false),
                    lang ? lang : 'en',
                    isNumeric);

                return resultDesc === 0 ? this.compareByConfig(a, b, configuration, lang, key + 1) : resultDesc;
            }
        }
    }

    getPropertyValueFromConceptsProps(uri, property, lang, props, defaultOnURI = true) {

        let propertyText = "";

        if (props) {

            let propertyValues = props[property];

            if (propertyValues) {
                if (Array.isArray(propertyValues) || typeof propertyValues === "object") {
                    let propertyValueByLang = this.getLabelByLangAlgorithm(propertyValues, lang);

                    if (propertyValueByLang && propertyValueByLang.value) {
                        propertyText = propertyValueByLang.value[0];
                    }
                } else if (propertyValues) {
                    propertyText = '' + propertyValues;
                }
            }
        }

        // No data is available for the label so take the last part of the URI.
        if (!propertyText && defaultOnURI) {
            let regexMatch = uri.match('([^\\/|#]+$)');
            return regexMatch === null ? uri : regexMatch[1];
        }

        return propertyText;
    }

    /**
     * - Si le libellé existe dans la langue sélectionnée via le sélecteur → On l'affichage.
     * - Sinon :
     *      - Si l'ordre des langues de la configuration par défaut de la terminologie est configurée → On affiche le libellé dans la langue paramétrée.
     *      - Sinon, si l'ordre des langues de la configuration par défaut de la terminologie n'existe pas ou le libéllé n'a pas été trouvé, on récupère l'ordre des langues de la configuration globale → On affiche le libellé dans la langue paramétrée.
     * - Sinon, si aucune n'est trouvée, on affiche le libellé en non localisé si disponible
     * - Sinon, si le libellé existe dans au moins une langue → On affiche le libellé dans la première langue disponible.
     */
    getLabelByLangAlgorithm(propertyValues, lang) {

        if (!propertyValues) return "";

        let label = "";
        if (propertyValues[lang]) return propertyValues[lang];

        const labelByLangOrder = (langOrder) => {
            if (langOrder && Object.keys(langOrder).length > 0) {
                Object.values(langOrder).every((el) => {
                    if (propertyValues[el]) {
                        label = propertyValues[el];
                        return false;
                    }
                    return true;
                });
            }
        }


        label = labelByLangOrder(this.state.concept && this.state.concept.default_termino && this.state.concept.default_termino.concepts_details && this.state.concept.default_termino.concepts_details.affichage ? this.state.concept.default_termino.concepts_details.affichage.ordre_langues : null);
        if (label) return label;

        label = labelByLangOrder(this.state.default && this.state.default.default_termino && this.state.default.default_termino.concepts_details && this.state.default.default_termino.concepts_details.affichage ? this.state.default.default_termino.concepts_details.affichage.ordre_langues : null);
        if (label) return label;

        if (Object.keys(propertyValues).length > 0) {
            if (propertyValues[""]) {
                return propertyValues[""];
            }
            return propertyValues[Object.keys(propertyValues)[0]];
        }
    }

    recupValueUriForLang(keyAttribute, othersAttributes) {
        // On cherche l'attribut dans la 'lang' selectionné puis suivant l'ordre des langues, ou non localisé.
        let retour = "";
        if (othersAttributes) {
            let localizedValue = this.findOtherByLang(othersAttributes, keyAttribute, VueApp.$route.query.lang);
            if (localizedValue) {
                if (localizedValue.value && localizedValue.value.length > 0 && localizedValue.value[0].length > 0) {
                    retour = localizedValue.value[0];
                } else if (localizedValue.valueUrisString) {
                    retour = localizedValue.valueUrisString;
                }
            }
            // prendre la bonne localisation
            if (!retour) {
                let langOrder = this.state.concept && this.state.concept.default_termino && this.state.concept.default_termino.concepts_details && this.state.concept.default_termino.concepts_details.affichage ? this.state.concept.default_termino.concepts_details.affichage.ordre_langues : null;

                if (!langOrder || Object.keys(langOrder).length === 0) {
                    langOrder = this.state.default && this.state.default.default_termino && this.state.default.default_termino.concepts_details && this.state.default.default_termino.concepts_details.affichage ? this.state.default.default_termino.concepts_details.affichage.ordre_langues : null;
                }

                if (langOrder && Object.keys(langOrder).length > 0) {
                    Object.values(langOrder).every((el) => {
                        let curentLangValue = this.findOtherByLang(othersAttributes, keyAttribute, el);
                        if (curentLangValue) {
                            if (curentLangValue.value && curentLangValue.value.length > 0 && curentLangValue.value[0].length > 0) {
                                retour = curentLangValue.value[0];
                            } else if (curentLangValue.valueUrisString) {
                                retour = curentLangValue.valueUrisString;
                            } else {
                                retour = "";
                            }
                            return false;
                        }
                        return true;
                    });
                }

            }
            // si toujours pas l'info prendre la version non localisée
            if (!retour) {
                let unlocalizedValue = this.findOtherByLang(othersAttributes, keyAttribute, '');
                if (unlocalizedValue) {
                    if (unlocalizedValue.value && unlocalizedValue.value.length > 0 && unlocalizedValue.value[0].length > 0) {
                        retour = unlocalizedValue.value[0];
                    } else if (unlocalizedValue.valueUrisString) {
                        retour = unlocalizedValue.valueUrisString;
                    }
                }
            }
        }
        return retour;
    }

    formatValueWithStructure(valueToFormat, currentStruct, currentTerminologyId, lang) {
        const REGEX_URL = new RegExp('^(https?:\\/\\/)', 'i');

        const keysVariable = Object.keys(currentStruct.variables);
        const valuesVariable = Object.values(currentStruct.variables);

        var allValueFormat = [];
        let terminologyLabelInPattern = false;
        var cptToFormat, oneValueFormat, oneValueHref, curToFormat, cptVariable, shortName, longName, valRecup, nbrUri, terminoCase;
        for (cptToFormat = 0; cptToFormat < valueToFormat.length; cptToFormat++) {
            curToFormat = valueToFormat[cptToFormat];
            oneValueFormat = currentStruct.pattern;

            nbrUri = 0;
            for (cptVariable = 0; cptVariable < keysVariable.length; cptVariable++) {
                if (valuesVariable[cptVariable].value == 'terminologyId') {
                    terminoCase = this.getTextWithCase(curToFormat.terminologyName, valuesVariable[cptVariable].case, lang);
                    oneValueFormat = oneValueFormat.replace(keysVariable[cptVariable], terminoCase);
                } else if (valuesVariable[cptVariable].value == 'terminologyVersionInfo') {
                    terminoCase = this.getTextWithCase(curToFormat.terminologyVersion, valuesVariable[cptVariable].case, lang);
                    oneValueFormat = oneValueFormat.replace(keysVariable[cptVariable], terminoCase);
                } else if (valuesVariable[cptVariable].value == 'terminologyLabel') {
                    // cas spécial pour ne pas repeter 2 fois le nom court de la terminologie
                    terminologyLabelInPattern = true;
                    terminoCase = this.getTextWithCase(curToFormat.terminologyTitle, valuesVariable[cptVariable].case, lang);
                    oneValueFormat = oneValueFormat.replace(keysVariable[cptVariable], terminoCase);
                } else if (valuesVariable[cptVariable].value == 'terminologyPublisher') {
                    terminoCase = this.getTextWithCase(curToFormat.terminologyPublisher, valuesVariable[cptVariable].case, lang);
                    oneValueFormat = oneValueFormat.replace(keysVariable[cptVariable], terminoCase);
                } else {
                    // cas général
                    [shortName, longName] = this.getShortLongName(valuesVariable[cptVariable].value)

                    var curVarVal = curToFormat.others.find(e => {
                        return e.key == shortName || e.key == longName;
                    });
                    if (curVarVal != null && curVarVal != undefined) {
                        valRecup = this.recupValueUriForLang(longName, curToFormat.others);
                        if (!valRecup) {
                            valRecup = curToFormat.uri;
                            nbrUri++;
                        } else if (REGEX_URL.test(valRecup)) {
                            nbrUri++;
                        }
                        if (valRecup) {
                            valRecup = this.getTextWithCase(valRecup, valuesVariable[cptVariable].case, lang);
                        }
                        oneValueFormat = oneValueFormat.replace(keysVariable[cptVariable], valRecup);
                    } else {
                        if (longName == BASIC_ATTRIBUTES.label) {
                            oneValueFormat = oneValueFormat.replace(keysVariable[cptVariable], curToFormat.uri);
                        } else {
                            oneValueFormat = oneValueFormat.replace(keysVariable[cptVariable], ' ');
                        }
                        nbrUri++;
                    }
                }
            }

            // on a que des uri
            if (nbrUri >= keysVariable.length || (nbrUri == keysVariable.length && terminologyLabelInPattern)) {
                // tenter de récupérer un label
                oneValueFormat = this.recupValueUriForLang(BASIC_ATTRIBUTES.label, curToFormat.others);
            }

            // rien => juste l'uri (une fois)
            if (!oneValueFormat) {
                oneValueFormat = curToFormat.uri;
            }

            // href
            if (curToFormat.concept) {
                // Link is relative to a concept so redirect to a concept page
                let terminologyIdForUri = curToFormat.terminologyName || this.store.terminologyId;
                oneValueHref = '<a href="/' + Vue.prototype.$searchResultManager.conceptsCatalogSlug + '/' + encodeURIComponent(terminologyIdForUri) + '/?lang=' + VueApp.$route.query.lang + '&concept=' + encodeURIComponent(curToFormat.uri) + '&treeViewType=' + VueApp.$route.query.treeViewType + '">' + oneValueFormat + '</a>';
                if (!terminologyLabelInPattern && curToFormat.terminologyName != currentTerminologyId) {
                    // Cas où le concept n'appartient à la terminologie courante
                    oneValueHref = oneValueHref + " (" + curToFormat.terminologyTitle + ")";
                }
            } else {
                // Link is not relative to a concept so adapt the redirect accordingly.
                oneValueHref = '<a href="/' + encodeURIComponent(curToFormat.terminologyName) + '/">' + oneValueFormat + '</a>';
            }
            allValueFormat[cptToFormat] = oneValueHref;
        }

        return allValueFormat;
    }


    /**
     * 
     * @param {*} others 
     */
    extractAllLabelsFromOthers(others) {
        const mapLabels = new Map();
        if (others){
            others.forEach((other) => {
                if (other.key === BASIC_ATTRIBUTES.label) {
                    mapLabels.set(other.lang, other.literals[0].value);
                }
            });
        }
        return mapLabels;
    }


    /**
     * Format value URI depending on rules.
     */
    formatValueUri(value, currentTerminologyId, casing, lang, axiomAnnotations, number) {
        let blocNumber = number? number : 0;
        let $self = this;
        let uris=[];
        // On cherche le libellé dans la 'lang' selectionné puis suivant l'ordre des langues, ou non localisé, ou fin de l'URI sinon.
        let label = this.recupValueUriForLang(BASIC_ATTRIBUTES.label, value.others);
        //if the label is not found, we display the last part of the URI
        if (!label) {
            label = value.uri;
        }
        if (label) {
            label = this.getTextWithCase(label, casing, lang);
        }
        if (!label) {
            label = value.uri;
        }
        // Extraction de la terminologie
        let externalTerminologie;
        if (value.terminologyName != currentTerminologyId) {
            externalTerminologie = value.terminologyTitle;
        }

        // Quand il n'y pas de others c'est que c'est non localisé
        if (!value.others || value.others.length ==0){
            uris.push({
                blocNumber : blocNumber,
                type : "uri",
                localise : false,
                lang: "",
                content:  value.terminologyName ? $self.formatConceptUrl(value,externalTerminologie, label) : $self.formatValueLiteralsFromContent(label),
                axiomAnnotations: axiomAnnotations == undefined ? [] : axiomAnnotations,
                individual: value.isIndividual
            })
        }

        // extraction des lables à partir du others
        let mapLabels =  this.extractAllLabelsFromOthers(value.others);

        // Parse mapLabels
        mapLabels.forEach((label, lang) => {                   
            uris.push({
                blocNumber : blocNumber,
                type : "uri",
                localise : true,
                lang: lang!=undefined ? lang : "",
                content: $self.formatConceptUrl(value,externalTerminologie, label),
                axiomAnnotations: axiomAnnotations == undefined ? [] : axiomAnnotations,
                individual: value.isIndividual
            })
        });
        
        // Quand il y a des others mais qui ne sont pas des labels
        if (!uris || uris.length ==0){
            uris.push({
                blocNumber : blocNumber,
                type : "uri",
                localise : false,
                lang: "",
                content:  value.terminologyName ? $self.formatConceptUrl(value,externalTerminologie, label) : $self.formatValueLiteralsFromContent(label),
                axiomAnnotations: axiomAnnotations == undefined ? [] : axiomAnnotations,
                individual: value.isIndividual
            })
        }
        return uris;
    }

    formatConceptUrl(value, externalTerminologie, label){
        let url="";
        if (value.concept || value.isIndividual) {
            // Link is relative to a concept so redirect to a concept page
            let terminologyIdForUri = value.terminologyName || this.store.terminologyId;
            if (externalTerminologie && externalTerminologie.length > 0) {
                // Cas où le concept n'appartient à la terminologie courante
                url = '<a href="/' + Vue.prototype.$searchResultManager.conceptsCatalogSlug + '/' + encodeURIComponent(terminologyIdForUri) + '/?lang=' + VueApp.$route.query.lang + '&concept=' + encodeURIComponent(value.uri) + '&treeViewType=' + VueApp.$route.query.treeViewType + '">' + label + '</a>' + '<span class="normal-font"> (' + externalTerminologie + ')</span>';
            } else {
                url = '<a href="/' + Vue.prototype.$searchResultManager.conceptsCatalogSlug + '/' + encodeURIComponent(terminologyIdForUri) + '/?lang=' + VueApp.$route.query.lang + '&concept=' + encodeURIComponent(value.uri) + '&treeViewType=' + VueApp.$route.query.treeViewType + '">' + label + '</a>';
            }
        } else {
            // Link is not relative to a concept so adapt the redirect accordingly.
            url = '<a href="/' + encodeURIComponent(value.terminologyName) + '/">' + label + '</a>';
        }
        return url;
    }
    /**
     * Converti une chaine en lien si elle ressemble à une URI.
     * @param {*} content 
     * @param {*} label 
     * @returns 
     */
    formatValueLiteralsFromContent(content, label) {
        // Si la string ressemble à une URI, on la transforme en lien
        let display = label !== undefined ? label : content;
        let formattedContent = content;
        if (content && (content.startsWith('http://') || content.startsWith('https://'))) {
            formattedContent = '<a href="' + content + '" target="_blank" rel="noopener noreferrer">' + display + '</a>';
        }
        return formattedContent;
    }


    getTextWithCase(text, casing, lang) {
        if (text) {
            text = this.translateBoolean(text, lang);
            if (casing != 'none') {
                switch (casing) {
                    case "lower":
                        text = text.toLowerCase();
                        break;
                    case "upper":
                        text = text.toUpperCase();
                        break;
                    case "camel":
                        text = Utils.camelize(text);
                        break;
                    default:
                }
            }
        }
        return text;
    }

    translateBoolean(value, lang) {
        if (value) {
            if (value === 1) { value === 'true'; }
            if (value === 0) { value === 'false'; }
            if (value === 'true' || value === 'false') {
                return TRANSLATIONS.boolean[value][lang] || TRANSLATIONS.boolean[value]["en"];
            }
        }
        return value;
    }

    findOtherByLang(others, keyAttribute, lang) {
        return others.find(e => {
            return e.key == keyAttribute
                && ((lang == '' && (!e.lang || e.lang == '')) || e.lang === lang);
        });
    }

    getMultilingualValueByLangAlgorithm(multilingualValue, lang, conceptUri) {

        if (!multilingualValue || multilingualValue.length === 0) {
            return conceptUri ? conceptUri : "";
        }

        if (multilingualValue.filter((el) => el.lang === lang)[0]) {
            return multilingualValue.filter((el) => el.lang === lang)[0].values[0];
        }

        let langOrder = this.state.concept?.default_termino?.concepts_details?.affichage?.ordre_langues;

        if (!langOrder || Object.keys(langOrder).length === 0) {
            langOrder = this.state.default?.default_termino?.concepts_details?.affichage?.ordre_langues;
        }

        if (langOrder && Object.keys(langOrder).length > 0) {

            let label;
            Object.values(langOrder).every((langEl) => {
                if (multilingualValue.filter((value) => value.lang === langEl)[0]) {
                    label = multilingualValue.filter((value) => value.lang === langEl)[0].values[0]
                    return false;
                }
                return true;
            });

            if (label) return label;
        }

        if (multilingualValue.filter((el) => el.lang === "")[0]) {
            return multilingualValue.filter((el) => el.lang === "")[0].values[0];
        }

        if (conceptUri) {
            return conceptUri;
        }

        return multilingualValue[0].values[0];
    }

    getConceptTargetUrl(conceptUri, terminologyId, hostedBySMT, conceptsCatalogSlug, query) {
        return hostedBySMT
            ? '/' + conceptsCatalogSlug + '/' + encodeURIComponent(terminologyId) + '/?lang=' + query.lang + '&concept=' + encodeURIComponent(conceptUri) + '&treeViewType=' + query.treeViewType
            : conceptUri;
    }

    sortAttributeValueByUri(valueUris, attributeUri, conf, lang) {
        if (valueUris?.length && this.hasValuesOrder(conf, attributeUri, 'uri')) {
            const order = this.getAttributeValuesOrder(conf, attributeUri)?.ordre;
            if (order === 'desc') {
                valueUris.sort((a, b) => b.uri.localeCompare(a.uri, lang, { numeric: true }));
            } else {
                valueUris.sort((a, b) => a.uri.localeCompare(b.uri, lang, { numeric: true }));
            }
        }
    }

    sortAttributeValueByLiteral(values, attributeUri, conf, lang) {
        if (this.hasValuesOrder(conf, attributeUri, 'literal')) {
            const order = this.getAttributeValuesOrder(conf, attributeUri)?.ordre;
            this.sortLiteralValues(values, order, lang);
        }
    }

    sortBlankNodesParCritere(blanknodes, critere) {
        let $self = this;
        let blanknodesTri = blanknodes.sort((a, b) => {
            const contentA = a.other.find(o => o.key === critere.critere)?.literals[0]?.content || '';
            const contentB = b.other.find(o => o.key === critere.critere)?.literals[0]?.content || '';
            return $self.literalValueCompare(contentA , contentB , critere.ordre, critere.lang);
        });
        return blanknodesTri
    }


    sortBlankNodesParConf(blanknodes, attributeUri, conf) {
        if (this.hasValuesOrder(conf, attributeUri, 'annotation')) {
            let tri = this.getBlanknodesOrder(conf, attributeUri);
            let ordrePredicat = tri.ordre_predicat_annotation;
            ordrePredicat.slice().reverse().forEach(ordre => {
                blanknodes = this.sortBlankNodesParCritere(blanknodes, ordre);
            });
        }
    }

    sortAttributeLabelByLiteral(values, attributeUri, conf, lang) {
        if (this.hasValuesOrder(conf, attributeUri, 'literal')) {
            const order = this.getAttributeValuesOrder(conf, attributeUri)?.ordre;
            if (order === 'desc') {
                values.sort((a, b) => Utils.cleanString(this.getLabel(b, lang), false).localeCompare(Utils.cleanString(this.getLabel(a, lang), false), lang, { numeric: true }));
            } else {
                values.sort((a, b) => Utils.cleanString(this.getLabel(a, lang), false).localeCompare(Utils.cleanString(this.getLabel(b, lang), false), lang, { numeric: true }));
            }
        }
    }

    getLabel(attribute, lang) {
        return (lang === 'en') ?
            attribute.labelEn || attribute.labelFr :
            attribute.labelFr || attribute.labelEn || this.extractUriLastPart(attribute.uri);
    }

    extractUriLastPart(uri) {
        const match = uri?.match('[#/]([^#/]*)$'); // Cherche la dernière partie après / ou #
        return match ? match[1] : uri || ""; // Retourne le match, ou l'entrée si aucun match, ou une chaîne vide si null/undefined
    }

    /**
     * Trie les éléments du tableau `others` selon les priorités :
     * 1. Les objets avec des `literals` non vides en premier.
     * 2. Ceux avec des `values` non vides ensuite.
     * 3. Les autres en dernier.
     * 
     * @param {Array<Object>} othersArray - Tableau d'objets à trier.
     * @returns {Array<Object>} - Tableau trié.
    */
    sortOthers(othersArray) {
        return othersArray.sort((a, b) => {
            if (a.literals.length > 0 && b.literals.length === 0) return -1;
            if (a.literals.length === 0 && b.literals.length > 0) return 1;
            return 0;
        });
    }

    /**
     * Tri des valeurs d'attributs de type literals.
     * - On compare les valeurs entre elles sous forme de chaînes de caractères, en prennant en compte la forme numérique.
     * - On tri les valeurs qui peuvent être parsée sous forme de date de façon chronologique, mais après les litéraux (dans l'ordre croissant).
     * 
     * @param {*} array 
     * @param {*} order 
     * @param {*} lang 
     */
    sortLiteralValues(array, order, lang) {
        let $self = this;
        array.sort((a, b) => {
            return $self.literalValueCompare(a.content, b.content, order, lang);
        });
        return array;
    }

    /**
     * Tri des valeursentre 2 literals. Cela prend en compte les dates et les chaînes de caractères.
     * @param {*} a 
     * @param {*} b 
     * @param {*} order 
     * @param {*} lang 
     * @returns 
     */
    literalValueCompare(a, b, order, lang) {
        const date_a = Utils.parseLiteralAsDate(a);
        const date_b = Utils.parseLiteralAsDate(b);
        let compare = 0;
        if (date_a && date_b) {
            // Si les deux dates sont valides, on les compare
            compare = date_a - date_b;
        } else if (date_a) {
            // Si seule la valeur a est une date valide, elle doit être positionée après la valeur non date
            compare = 1;
        } else if (date_b) {
            // Si seule la valeur b est une date valide, elle doit être positionée après la valeur non date
            compare = -1;
        } else {
            if (lang !== undefined && lang !== null && lang !== "") {
                compare = Utils.cleanString(a, false).localeCompare(Utils.cleanString(b, false), lang, { numeric: true });
            } else {
                compare = Utils.cleanString(a, false).localeCompare(Utils.cleanString(b, false), { numeric: true });
            }
        }
        return ((order === 'desc') ? -1 : 1) * compare;
    }


    hasValuesOrder(conf, attributeUri, type) {
        return this.getAttributeValuesOrder(conf, attributeUri)?.type === type;
    }


    getBlanknodesOrder(conf, attributeUri) {
        let tri = null;
        conf.concepts_details.affichage.ordre_valeurs.forEach(ordre_valeurs => {
            if (ordre_valeurs.type == "annotation" && ordre_valeurs.predicat !== undefined && (ordre_valeurs.predicat === attributeUri || ordre_valeurs.predicat === this.getBasicAttributeShortName(attributeUri))) {
                tri = ordre_valeurs;
            }
        });
        return tri;
    }

    getAttributeValuesOrder(conf, attributeUri) {
        let tri = null;
        conf.concepts_details.affichage.ordre_valeurs.forEach(ordre_valeurs => {
            if (ordre_valeurs.predicat !== undefined && (ordre_valeurs.predicat === attributeUri || ordre_valeurs.predicat === this.getBasicAttributeShortName(attributeUri))) {
                tri = ordre_valeurs;
            }
        });
        return tri;
    }

    /**
     * Return the shortname of an URI if this URI is a Basic Attribute, the unmodified URI otherwise.
     */
    getBasicAttributeShortName(attributeUri) {
        for (const shortName in BASIC_ATTRIBUTES) {
            if (BASIC_ATTRIBUTES[shortName] == attributeUri) {
                return shortName;
            }
        }
        return attributeUri
    }

    getShortLongName(searchName) {
        var res = [];
        if (searchName != null && searchName != undefined) {
            res = [searchName, searchName];
            for (const [shortN, longN] of Object.entries(BASIC_ATTRIBUTES)) {
                if (searchName == longN || searchName == shortN) {
                    res = [shortN, longN];
                    break;
                }
            }
        }
        return res;
    }

}

export default new StoreConfiguration();